import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, isSubscribed } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { ChevronLeft, Lock, Crown, Plus, Check, Loader2 } from "lucide-react";

export default function Watch() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const subscribed = isSubscribed(user);
  const [ep, setEp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [resumeFrom, setResumeFrom] = useState(0);
  const videoRef = useRef(null);
  const lastSave = useRef(0);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/episodes/${id}`)
      .then(r => { if (!cancel) setEp(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    if (user && user !== false) {
      api.get("/watchlist").then(r => {
        if (!cancel) setInWatchlist(r.data.some(e => e.id === id));
      }).catch(() => {});
      api.get(`/progress/${id}`).then(r => {
        if (!cancel) setResumeFrom(r.data?.position_seconds || 0);
      }).catch(() => {});
    }
    return () => { cancel = true; };
  }, [id, user]);

  // Seek to last position on metadata load
  const onLoaded = useCallback(() => {
    if (videoRef.current && resumeFrom > 5 && resumeFrom < (videoRef.current.duration - 5)) {
      videoRef.current.currentTime = resumeFrom;
      toast.info(`Resuming from ${Math.floor(resumeFrom / 60)}:${String(Math.floor(resumeFrom % 60)).padStart(2, "0")}`);
    }
  }, [resumeFrom]);

  // If /progress resolves AFTER loadedmetadata already fired, seek imperatively.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || resumeFrom <= 5) return;
    if (v.readyState >= 1 && v.duration && resumeFrom < v.duration - 5 && v.currentTime < 1) {
      v.currentTime = resumeFrom;
      toast.info(`Resuming from ${Math.floor(resumeFrom / 60)}:${String(Math.floor(resumeFrom % 60)).padStart(2, "0")}`);
    }
  }, [resumeFrom, ep]);

  // Throttled save every 5s
  const onTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.duration || isNaN(v.duration)) return;
    const now = Date.now();
    if (now - lastSave.current < 5000) return;
    lastSave.current = now;
    api.post(`/progress/${id}`, {
      position_seconds: v.currentTime,
      duration_seconds: v.duration,
    }).catch(() => {});
  }, [id]);

  // Save on pause / ended / unmount
  const flushSave = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.duration || isNaN(v.duration)) return;
    api.post(`/progress/${id}`, {
      position_seconds: v.currentTime,
      duration_seconds: v.duration,
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    return () => flushSave();
  }, [flushSave]);

  const toggleWatchlist = async () => {
    try {
      if (inWatchlist) {
        await api.delete(`/watchlist/${id}`);
        setInWatchlist(false);
        toast.success("Removed from watchlist");
      } else {
        await api.post(`/watchlist/${id}`);
        setInWatchlist(true);
        toast.success("Added to watchlist");
      }
    } catch {
      toast.error("Something went wrong");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg text-white">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        </div>
      </div>
    );
  }
  if (!ep) {
    return (
      <div className="min-h-screen bg-brand-bg text-white">
        <Navbar />
        <div className="text-center pt-24">Episode not found.</div>
      </div>
    );
  }

  const locked = ep.locked || !ep.video_url;

  return (
    <div className="min-h-screen bg-brand-bg text-white">
      <Navbar />
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 pt-8 pb-20">
        <Link to="/browse" data-testid="watch-back-btn" className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-white uppercase tracking-[0.18em]">
          <ChevronLeft className="h-4 w-4" /> Back to browse
        </Link>

        <div className="mt-6 player-wrap aspect-video w-full border border-white/10 relative overflow-hidden" data-testid="watch-player">
          {locked ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black">
              <div className="absolute inset-0" style={{
                background: "radial-gradient(120% 100% at 0% 0%, rgba(255,0,85,0.25) 0%, rgba(5,5,5,1) 60%), linear-gradient(135deg, #1a0410 0%, #050505 100%)"
              }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
              <div className="relative z-10 text-center px-6">
                <Lock className="h-12 w-12 text-brand-primary mx-auto" />
                <h2 className="font-display text-3xl md:text-5xl uppercase mt-5 tracking-tight">Premium Episode</h2>
                <p className="text-brand-muted mt-3 max-w-md mx-auto">
                  Subscribe to BallOutTV for $6.99/month to unlock this and every episode.
                </p>
                <Link to="/subscribe" data-testid="watch-go-premium">
                  <Button className="mt-6 bg-brand-primary hover:bg-brand-primaryHover rounded-none px-7 h-12 uppercase tracking-[0.15em]">
                    <Crown className="h-4 w-4 mr-2" /> Go Premium · $6.99/mo
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <video
              ref={videoRef}
              key={ep.video_url}
              data-testid="watch-video-element"
              src={ep.video_url}
              controls
              autoPlay
              onLoadedMetadata={onLoaded}
              onTimeUpdate={onTimeUpdate}
              onPause={flushSave}
              onEnded={flushSave}
              className="w-full h-full bg-black"
            />
          )}
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-start">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-brand-primary mb-2">{ep.show} · Season {ep.season} · Episode {ep.episode_number}</div>
            <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight">{ep.title || "TBA"}</h1>
            <p className="text-brand-muted mt-4 max-w-2xl leading-relaxed">{ep.description || "TBA"}</p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="watch-watchlist-toggle"
              onClick={toggleWatchlist}
              variant="outline"
              className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-none h-11 uppercase tracking-[0.15em]"
            >
              {inWatchlist ? <><Check className="h-4 w-4 mr-2" /> In list</> : <><Plus className="h-4 w-4 mr-2" /> My list</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
